import type { SetupChecklistItemState } from "@unbox/api";
import { CheckDone01, DotsHorizontal, EyeOff, Minus } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import type { SetupItemDefinition } from "../lib/setup-items";

const MENU_FLIP_THRESHOLD = 130;

export type SetupRowActions = {
  visit: () => void;
  complete: () => void;
  ignore: () => void;
  unignore: () => void;
};

type Props = {
  definition: SetupItemDefinition;
  state: SetupChecklistItemState;
  dark: boolean;
  actorIsMe: boolean;
  canComplete: boolean;
  actions: SetupRowActions;
};

const completionNote = (state: SetupChecklistItemState, actorIsMe: boolean) => {
  const prefix = state.manual && state.completedBy ? "Marked done · " : "Done · ";
  if (!state.completedBy) return `${prefix}by your team`;
  return `${prefix}${actorIsMe ? "by you" : `by ${state.completedBy.name}`}`;
};

export function SetupChecklistRow({ definition, state, dark, actorIsMe, canComplete, actions }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [flipMenu, setFlipMenu] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const done = state.status === "done";
  const muted = dark ? "text-white/60" : "text-gray-500";

  if (done || state.ignored) {
    return (
      <div
        className={cx(
          "flex items-center gap-2.5 rounded-lg border px-3 py-1.5",
          dark ? "border-white/8 bg-white/3" : "border-gray-200 bg-white",
        )}
      >
        <span
          aria-hidden
          className={cx(
            "inline-flex size-3.5 shrink-0 items-center justify-center rounded-full",
            done
              ? dark
                ? "bg-success-500/20 text-[#32d583]"
                : "bg-success-50 text-success-600"
              : dark
                ? "bg-white/10 text-white/55"
                : "bg-gray-100 text-gray-500",
          )}
        >
          {done ? <CheckDone01 className="size-[9px]" /> : <Minus className="size-[9px]" strokeWidth={3} />}
        </span>

        <p
          className={cx(
            "min-w-0 flex-1 truncate text-[13px] leading-[18px]",
            done && "line-through",
            dark ? "text-white/45" : "text-gray-400",
          )}
        >
          {definition.label}
        </p>

        {done ? (
          <span className={cx("shrink-0 text-xs leading-4", muted)}>{completionNote(state, actorIsMe)}</span>
        ) : (
          <span className={cx("inline-flex shrink-0 items-center gap-1.5 text-xs leading-4", muted)}>
            <span>Ignored ·</span>
            <button type="button" onClick={actions.unignore} className="cursor-pointer underline underline-offset-2">
              Undo
            </button>
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      ref={rowRef}
      className={cx(
        "setup-row relative flex items-center gap-3 rounded-xl border py-2.5 pr-2.5 pl-3",
        dark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white shadow-xs",
      )}
    >
      <span
        aria-hidden
        className={cx(
          "inline-flex size-8 shrink-0 items-center justify-center rounded-[9px]",
          dark ? "bg-[#c0a7f2]/14 text-[#c0a7f2]" : "bg-[#efe9fb] text-[#6b4fc0]",
        )}
      >
        <definition.icon className="size-[18px]" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className={cx("text-sm font-medium", dark ? "text-white" : "text-gray-900")}>{definition.label}</p>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          aria-label="More options"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => {
            const row = rowRef.current;
            const scroller = row?.closest(".overflow-y-auto");
            const rowBottom = row?.getBoundingClientRect().bottom;
            const limit = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight;
            setFlipMenu(rowBottom !== undefined && limit - rowBottom < MENU_FLIP_THRESHOLD);
            setMenuOpen((open) => !open);
          }}
          className={cx(
            "setup-row-more inline-flex size-8 cursor-pointer items-center justify-center rounded-[9px]",
            menuOpen && "is-open",
            dark ? "text-white/60" : "text-gray-500",
          )}
        >
          <DotsHorizontal className="size-4" strokeWidth={2.4} />
        </button>

        <Button color="secondary" size="sm" className="setup-cta" onClick={actions.visit}>
          {definition.cta}
        </Button>
      </div>

      {menuOpen && (
        <div
          role="menu"
          className={cx(
            "setup-menu absolute right-2.5 z-20 flex min-w-56 flex-col rounded-[10px] border p-1",
            flipMenu ? "bottom-[calc(100%-4px)]" : "top-[calc(100%-4px)]",
            dark
              ? "border-white/12 bg-[#3a2a48] shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)]"
              : "border-gray-200 bg-white shadow-[0_12px_32px_-12px_rgba(16,24,40,0.25)]",
          )}
        >
          {state.manual && canComplete && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                actions.complete();
              }}
              className={cx(
                "flex cursor-pointer items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-left text-[13px] leading-[18px]",
                dark ? "text-white" : "text-gray-900",
              )}
            >
              <span className={cx("inline-flex", muted)}>
                <CheckDone01 className="size-[15px]" />
              </span>
              <span>
                Mark as completed
                <small className={cx("block text-[11px] leading-[14px]", muted)}>
                  For everyone in your organisation
                </small>
              </span>
            </button>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              actions.ignore();
            }}
            className={cx(
              "flex cursor-pointer items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-left text-[13px] leading-[18px]",
              dark ? "text-white" : "text-gray-900",
            )}
          >
            <span className={cx("inline-flex", muted)}>
              <EyeOff className="size-[15px]" />
            </span>
            <span>
              Ignore this step
              <small className={cx("block text-[11px] leading-[14px]", muted)}>Hide it from the checklist</small>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
